import { CUSTOM_OPTIONAL } from "./customFoods";
import type { CustomFood, Dish, DishItem, FoodEntry } from "./types";

/**
 * A dish the user cooks: its ingredients plus the cooked pot's weight (ADR-048; research
 * user-demand.md §5 #1, market-gaps.md §3.3).
 *
 * Every tracker that has recipes divides by what went in. That is right for a salad and wrong for
 * anything cooked in water: 1,221 g of rajma, onion, tomato, oil and ghee became a 1,500 g pot, so
 * a katori of it carries 1,199 ÷ 1,500 of the energy, not 1,199 ÷ 1,221 — 120 kcal, not 147.
 * Dal gains water, a dry sabzi loses it; only the pot's own weight knows which. So the pot is
 * weighed and the sum is divided by that.
 *
 * The result is saved as an ordinary `CustomFood` whose `per100` is worked out here, once, at
 * save time. Entries logged from it copy their values like any other (invariant 18), and an edit
 * is a tombstone plus a new id (ADR-026), so a changed recipe never rewrites a meal already logged.
 */

/** The household portions a dish is eaten by, and the weight each starts at before it is weighed. */
export const DISH_UNITS = ["katori", "bowl", "plate"] as const;
export type DishUnit = (typeof DISH_UNITS)[number];
export const DISH_UNIT_G: Readonly<Record<DishUnit, number>> = { katori: 150, bowl: 250, plate: 300 };

type OptionalKey = (typeof CUSTOM_OPTIONAL)[number];

/** An ingredient as the dish keeps it: the picker's entry without the meal, the time, the id or
 *  an oil figure (oil in a dish is an ingredient of its own). */
export function dishItemOf(e: FoodEntry): DishItem {
  const item: DishItem = {
    name: e.name,
    qty: e.qty,
    unit: e.unit,
    grams: e.grams,
    kcal: e.kcal,
    p: e.p,
    c: e.c,
    f: e.f,
  };
  if (e.code) item.code = e.code;
  for (const k of CUSTOM_OPTIONAL) {
    const v = e[k];
    if (typeof v === "number") item[k] = v;
  }
  return item;
}

export interface DishSums {
  grams: number;
  kcal: number;
  p: number;
  c: number;
  f: number;
  /** a sum for each optional value every ingredient carries; absent when any lacks it */
  opt: Partial<Record<OptionalKey, number>>;
  /** for each optional value some ingredient lacks, the ingredients that lack it */
  lacking: Partial<Record<OptionalKey, string[]>>;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const r3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * What went into the pot. An optional value is summed only when every ingredient carries it:
 * a sum with a gap in it would be saved as the dish's figure and read as complete by every entry
 * copied from it, so a gap makes the value "not known" for the dish (invariant 18), and the sheet
 * names the ingredient that caused it.
 */
export function dishSums(items: DishItem[]): DishSums {
  const s: DishSums = { grams: 0, kcal: 0, p: 0, c: 0, f: 0, opt: {}, lacking: {} };
  for (const it of items) {
    s.grams += it.grams;
    s.kcal += it.kcal;
    s.p += it.p;
    s.c += it.c;
    s.f += it.f;
  }
  s.p = r1(s.p);
  s.c = r1(s.c);
  s.f = r1(s.f);
  for (const k of CUSTOM_OPTIONAL) {
    const lack = items.filter((it) => typeof it[k] !== "number").map((it) => it.name);
    if (!items.length) continue;
    if (lack.length) s.lacking[k] = lack;
    else s.opt[k] = r1(items.reduce((a, it) => a + (it[k] ?? 0), 0));
  }
  return s;
}

/**
 * Per 100 g of the cooked dish: the pot's sums divided by the pot's weight. Kept to three decimal
 * places rather than the label foods' one, so a katori logged from it reads what the sheet showed
 * (5.8 g of protein, not 5.9 from a rounded 3.9). Null without ingredients or a pot weight.
 */
export function dishPer100(items: DishItem[], potG: number): CustomFood["per100"] | null {
  if (!items.length || !Number.isFinite(potG) || potG <= 0) return null;
  const s = dishSums(items);
  const x = 100 / potG;
  const per: CustomFood["per100"] = { k: r3(s.kcal * x), p: r3(s.p * x), c: r3(s.c * x), f: r3(s.f * x) };
  for (const k of CUSTOM_OPTIONAL) {
    const v = s.opt[k];
    if (typeof v === "number") per[k] = r3(v * x);
  }
  return per;
}

/** How many portions the pot held, to a half: "The pot holds 10 katori". Null without both. */
export function potHolds(potG: number, portionG: number): number | null {
  if (!(potG > 0) || !(portionG > 0)) return null;
  return Math.round((potG / portionG) * 2) / 2;
}

/** kcal a portion would read if the sum were divided by what went in instead of the pot — the
 *  figure every recipe builder that ignores the water gives, shown so the difference is seen. */
export function wentInKcal(items: DishItem[], portionG: number): number | null {
  const s = dishSums(items);
  if (!(s.grams > 0) || !(portionG > 0)) return null;
  return Math.round((s.kcal / s.grams) * portionG);
}

/** Household words that read the same for one or many — "4 katori", "2 chapati". */
const SAME_PLURAL = new Set(["g", "katori", "chapati", "roti", "idli", "dosa", "paratha", "parantha", "poori", "puri", "papad"]);

/** "2 tablespoons", "3 tomatoes", "4 katori", "1 teaspoon": how much, as the dish sheet lists it. */
export function portionWords(qty: number, unit: string): string {
  const q = qty.toLocaleString();
  if (qty === 1 || SAME_PLURAL.has(unit.toLowerCase())) return `${q} ${unit}`;
  if (/(?:s|sh|ch|x|o)$/i.test(unit)) return `${q} ${unit}es`;
  if (/[^aeiou]y$/i.test(unit)) return `${q} ${unit.slice(0, -1)}ies`;
  return `${q} ${unit}s`;
}

/** What the dish sheet holds while it is filled in: every box as typed. */
export interface DishDraft {
  name: string;
  items: DishItem[];
  pot: string;
  unit: DishUnit;
  g: string;
}

export const EMPTY_DISH: DishDraft = { name: "", items: [], pot: "", unit: "katori", g: String(DISH_UNIT_G.katori) };

/** A number as typed, allowing the thousands comma the board shows ("1,500"). */
export function typedNumber(s: string): number {
  const t = s.replace(/,/g, "").trim();
  return t ? Number(t) : Number.NaN;
}

/** The draft for editing a saved dish, or null for a food that is not one. */
export function dishDraftOf(f: CustomFood): DishDraft | null {
  if (!f.dish) return null;
  const unit = (DISH_UNITS as readonly string[]).includes(f.unit) ? (f.unit as DishUnit) : "katori";
  return { name: f.name, items: f.dish.items.map((x) => ({ ...x })), pot: String(f.dish.potG), unit, g: String(f.g) };
}

/**
 * The draft as a food ready for `addCustomFood`, or the first thing stopping it, in words for the
 * ActionBar. The two sanity limits are the label sheet's: nothing edible passes 900 kcal or 100 g
 * of macronutrients in 100 g — here they catch a pot weight typed short by a digit.
 */
export function readDish(
  d: DishDraft
): { food: Omit<CustomFood, "id" | "at">; reason?: undefined } | { food?: undefined; reason: string } {
  const name = d.name.trim();
  if (!name) return { reason: "Name it" };
  if (!d.items.length) return { reason: "Add what went in" };
  const pot = typedNumber(d.pot);
  if (!Number.isFinite(pot) || pot <= 0) return { reason: "Weigh the cooked pot" };
  const g = typedNumber(d.g);
  if (!Number.isFinite(g) || g <= 0) return { reason: `What one ${d.unit} weighs` };
  if (g > pot) return { reason: `One ${d.unit} is more than the whole pot` };
  const per100 = dishPer100(d.items, pot);
  if (!per100) return { reason: "Weigh the cooked pot" };
  if (per100.k > 900) return { reason: "Over 900 kcal per 100 g — check the pot’s weight" };
  if (per100.p + per100.c + per100.f > 100)
    return { reason: "Over 100 g of protein, carbs and fat per 100 g — check the pot’s weight" };
  const dish: Dish = { items: d.items.map((x) => ({ ...x })), potG: Math.round(pot) };
  return { food: { name: name.slice(0, 80), unit: d.unit, g: Math.round(g), per100, dish } };
}
